import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
    Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
    Paper, Button, TextField, Box, Typography, CircularProgress, Alert,
    Snackbar, Stack, alpha, useTheme, FormControl, InputLabel, Select,
    MenuItem, Tooltip, IconButton, Chip, Collapse, Grid,
} from '@mui/material';
import {
    Search as SearchIcon, FileDownloadOutlined as ExportIcon,
    PrintOutlined as PrintIcon,
    InfoOutlined as InfoIcon, FilterList as FilterIcon,
    VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useReactToPrint } from 'react-to-print';
import AttendanceDetailsModal from './AttendanceDetailsModal';

// --- Helper Functions ---
const generateDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dateArray = [];
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return dateArray;
    let currentDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const finalEndDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (currentDate <= finalEndDate) {
        dateArray.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
};

const getAttendanceColor = (status, theme) => {
    const s = status?.toUpperCase() || '';
    return {
        P: alpha(theme.palette.success.light, 0.3),
        A: alpha(theme.palette.error.light, 0.3),
        L: alpha(theme.palette.warning.light, 0.4),
        H: alpha(theme.palette.info.light, 0.3),
    }[s] || 'transparent';
};

const getAttendanceTooltip = (status) => ({
    P: 'Present', A: 'Absent', L: 'Leave', H: 'Holiday',
}[status?.toUpperCase()] || 'Unknown');

const formatHeaderDate = (dateObj) => dateObj && !isNaN(dateObj.getTime()) ? format(dateObj, 'dd MMM') : '';
const getDateKey = (dateObj) => dateObj && !isNaN(dateObj.getTime()) ? format(dateObj, 'yyyy-MM-dd') : '';

// --- Printable Component ---
const AttendancePrintableTable = React.forwardRef(({
    attendanceDetails, allDatesInRange, schoolName, className, sectionName, fromDate, toDate
}, ref) => {
    const theme = useTheme();
    if (!attendanceDetails?.length) return null;

    return (
        <div ref={ref} style={{ width: '100%', padding: '8mm' }}>
            <style type="text/css" media="print">{`
                @page { size: A4 landscape; margin: 8mm; }
                body { 
                    margin: 0; padding: 0; 
                    font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif; 
                    font-size: 6.5pt; 
                    color: #333; 
                }
                table { 
                    border-collapse: collapse; 
                    width: 100%; 
                    table-layout: auto; 
                    border: 0.5px solid #ddd; 
                }
                th, td { 
                    border: 0.5px solid #ddd; 
                    padding: 1.5px 2px; 
                    text-align: center; 
                    vertical-align: middle; 
                    font-size: 6.5pt; 
                }
                th { 
                    background-color: #e0e0e0 !important; 
                    font-weight: 700; 
                    font-size: 7pt; 
                }
                .print-header { 
                    text-align: center; 
                    margin-bottom: 6px; 
                }
                .print-header h4, .print-header p { 
                    margin: 0.5px 0; 
                }
                .text-left { text-align: left !important; }
                .alt-row { background-color: #f9f9f9 !important; }
                .footer { 
                    position: fixed; 
                    bottom: 8mm; 
                    width: 100%; 
                    text-align: center; 
                    font-size: 6pt; 
                    color: #666; 
                }
            `}</style>
            <Box className="print-header">
                <Typography variant="h6" component="h4" sx={{ fontSize: '10pt', fontWeight: 700 }}>{schoolName || 'Attendance Report'}</Typography>
                <Typography sx={{ fontSize: '8pt' }}>Class: {className || '-'} | Section: {sectionName || '-'}</Typography>
                <Typography sx={{ fontSize: '7pt' }}>Period: {fromDate} to {toDate}</Typography>
            </Box>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell className="text-left" sx={{ minWidth: 80 }}>Student</TableCell>
                        <TableCell sx={{ minWidth: 30 }}>Roll</TableCell>
                        {allDatesInRange.map((dateObj) => (
                            <TableCell key={`print-th-${getDateKey(dateObj)}`} sx={{ minWidth: 20, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                                {formatHeaderDate(dateObj)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {attendanceDetails.map((detail, index) => (
                        <TableRow key={`print-tr-${detail.studentId}`} className={index % 2 ? 'alt-row' : ''}>
                            <TableCell className="text-left">{detail.studentName?.trim()}</TableCell>
                            <TableCell>{detail.rollNumber || '-'}</TableCell>
                            {allDatesInRange.map((dateObj) => {
                                const dateKey = getDateKey(dateObj);
                                const dayStatus = detail.dailyAttendance?.[dateKey] || 'A';
                                return (
                                    <TableCell key={`print-td-${dateKey}-${detail.studentId}`} style={{ backgroundColor: getAttendanceColor(dayStatus, theme) }}>
                                        {dayStatus}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Typography className="footer">Generated on {format(new Date(), 'PPp')} | Page {1}</Typography>
        </div>
    );
});

// --- Main Component ---
const StudentAttendance = memo(({ iSchoolId }) => {
    const theme = useTheme();
    const printableComponentRef = useRef();
    const [filters, setFilters] = useState({ startDate: '', endDate: '', class: '', section: '' });
    const [classes, setClasses] = useState([]);
    const [allSections, setAllSections] = useState([]);
    const [sections, setSections] = useState([]);
    const [attendanceDetails, setAttendanceDetails] = useState([]);
    const [allDatesInRange, setAllDatesInRange] = useState([]);
    const [schoolName, setSchoolName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState({ name: '', roll: '', dailyAttendance: {} });
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const [isFetched, setIsFetched] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    const attendanceLegends = [
        { status: 'P', label: 'Present', color: getAttendanceColor('P', theme) },
        { status: 'A', label: 'Absent', color: getAttendanceColor('A', theme) },
        { status: 'L', label: 'Leave', color: getAttendanceColor('L', theme) },
        { status: 'H', label: 'Holiday', color: getAttendanceColor('H', theme) },
    ];

    useEffect(() => {
        if (!iSchoolId) return;
        setIsLoading(true);
        Promise.all([
            axiosInstance.get(`/api/classes`, { headers: { schoolId: iSchoolId } }).then(res => setClasses(Array.isArray(res.data) ? res.data : [])),
            axiosInstance.get(`/api/sections`, { headers: { schoolId: iSchoolId } }).then(res => setAllSections(Array.isArray(res.data) ? res.data : [])),
            axiosInstance.get(`/api/school-settings/school/${iSchoolId}`, { headers: { schoolId: iSchoolId } }).then(res => setSchoolName(res.data?.schoolName || 'Unknown School')),
        ]).catch(err => {
            setError(getApiErrorMessage(err, 'Failed to load initial data'));
            setClasses([]);
            setAllSections([]);
            setSchoolName('Error');
        }).finally(() => setIsLoading(false));
    }, [iSchoolId]);

    useEffect(() => {
        if (!filters.class || !iSchoolId) {
            setSections([]);
            return;
        }
        setAttendanceDetails([]);
        setError(null);
        setIsFetched(false);

        const selectedClass = classes.find((cls) => String(cls.classId) === String(filters.class));
        const assignedSectionIds = String(selectedClass?.assignedSections || '')
            .split(',')
            .map((value) => Number(String(value).trim()))
            .filter((value) => !Number.isNaN(value));

        const availableSections = allSections.filter((section) => assignedSectionIds.includes(Number(section.sectionId)));
        setSections(availableSections);

        if (!availableSections.some((sec) => String(sec.sectionId) === String(filters.section))) {
            setFilters(prev => ({ ...prev, section: '' }));
        }
    }, [allSections, classes, filters.class, filters.section, iSchoolId]);

    const showNotification = useCallback((message, severity = 'info') => {
        setNotification({ open: true, message, severity });
    }, []);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const newState = { ...prev, [name]: value };
            if (name === 'class' && value !== prev.class) newState.section = '';
            return newState;
        });
        if (name !== 'section') {
            setAttendanceDetails([]);
            setError(null);
            setIsFetched(false);
        }
    }, []);

    const fetchAttendanceDetails = useCallback(async () => {
        const { startDate, endDate, class: classId, section: sectionId } = filters;
        if (!classId || !sectionId || !startDate || !endDate) {
            showNotification('All filters are required', 'warning');
            setIsFetched(false);
            return;
        }
        const startDt = new Date(startDate);
        const endDt = new Date(endDate);
        if (isNaN(startDt.getTime()) || isNaN(endDt.getTime())) {
            showNotification('Invalid date format', 'error');
            setIsFetched(false);
            return;
        }
        if (startDt > endDt) {
            showNotification('From Date cannot be after To Date', 'warning');
            setIsFetched(false);
            return;
        }
        const diffDays = Math.ceil((endDt - startDt) / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays > 90) {
            showNotification('Date range cannot exceed 90 days', 'warning');
            setIsFetched(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        setIsFetched(true);
        try {
            const { data } = await axiosInstance.get('/api/class-attendance/details', {
                params: { classId, sectionId, startDate, endDate },
                headers: { schoolId: iSchoolId },
            });
            setAttendanceDetails(Array.isArray(data) ? data : []);
            setAllDatesInRange(generateDateRange(startDate, endDate));
            if (!data?.length) showNotification('No attendance records found', 'info');
        } catch (err) {
            const message = getApiErrorMessage(err, 'Failed to fetch attendance');
            setError({ message });
            showNotification(message, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [filters, iSchoolId, showNotification]);

    const exportToExcel = useCallback(() => {
        if (!attendanceDetails.length) {
            showNotification('No data to export', 'warning');
            return;
        }
        const headers = ['Student Name', 'Roll No.', ...allDatesInRange.map(formatHeaderDate)];
        const dataRows = attendanceDetails.map(detail => [
            detail.studentName || '',
            detail.rollNumber || '-',
            ...allDatesInRange.map(dateObj => detail.dailyAttendance?.[getDateKey(dateObj)] || 'A'),
        ]);
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
        worksheet['!cols'] = headers.map((_, i) => ({ wch: i === 0 ? 25 : 8 }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const filename = `Attendance_${filters.class}_${filters.section}_${filters.startDate}_to_${filters.endDate}.xlsx`;
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), filename);
        showNotification('Exported successfully', 'success');
    }, [attendanceDetails, allDatesInRange, filters, showNotification]);

    const handlePrint = useReactToPrint({
        content: () => printableComponentRef.current,
        documentTitle: `Attendance_${filters.class}_${filters.section}_${filters.startDate}_to_${filters.endDate}`,
        pageStyle: `@page { size: A4 landscape; margin: 8mm; }`,
        onBeforeGetContent: () => setIsLoading(true),
        onAfterPrint: () => setIsLoading(false),
    });

    const handleOpenModal = useCallback((studentName, rollNumber, dailyAttendance) => {
        const completeDailyAttendance = {};
        allDatesInRange.forEach(dateObj => {
            completeDailyAttendance[getDateKey(dateObj)] = dailyAttendance?.[getDateKey(dateObj)] || 'A';
        });
        setSelectedStudent({ name: studentName, roll: rollNumber, dailyAttendance: completeDailyAttendance });
        setModalOpen(true);
    }, [allDatesInRange]);

    const isDataAvailable = !isLoading && !error && attendanceDetails.length > 0;
    const isFiltersComplete = filters.class && filters.section && filters.startDate && filters.endDate;
    const showNoDataMessage = isFetched && !isLoading && !error && !attendanceDetails.length && isFiltersComplete;
    const showInitialMessage = !isFetched && !isLoading && !error && !isFiltersComplete;

    const headerCellStyle = {
        py: 0.5, px: 0.5, bgcolor: '#F5F7FB',
        fontWeight: 600, fontSize: '0.75rem', textAlign: 'center',
    };
    const bodyCellStyle = {
        py: 0.3, px: 0.5,
        fontSize: '0.7rem', textAlign: 'center',
    };
    const stickyColumnStyle = {
        position: 'sticky', left: 0, zIndex: 1000, bgcolor: theme.palette.background.paper,
        boxShadow: `2px 0 4px -2px ${alpha(theme.palette.common.black, 0.1)}`,
    };

    return (
        <Box sx={{ minHeight: '100%', p: 0, bgcolor: 'transparent' }}>
            <Paper elevation={0} variant="outlined" sx={{ p: 1.25, mb: 1.2, borderRadius: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Student Attendance Report
                    </Typography>
                    <IconButton onClick={() => setShowFilters(!showFilters)} aria-label="toggle filters">
                        <FilterIcon />
                    </IconButton>
                </Stack>
                <Collapse in={showFilters}>
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small" disabled={isLoading || !classes.length}>
                                <InputLabel>Class</InputLabel>
                                <Select label="Class" name="class" value={filters.class} onChange={handleFilterChange}>
                                    <MenuItem value=""><em>Select Class</em></MenuItem>
                                    {classes.map(cls => <MenuItem key={cls.classId} value={cls.classId}>{cls.className}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small" disabled={!filters.class || isLoading || !sections.length}>
                                <InputLabel>Section</InputLabel>
                                <Select label="Section" name="section" value={filters.section} onChange={handleFilterChange}>
                                    <MenuItem value=""><em>Select Section</em></MenuItem>
                                    {sections.map(sec => <MenuItem key={sec.sectionId} value={sec.sectionId}>{sec.sectionName}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth label="From Date" type="date" name="startDate" size="small"
                                value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }}
                                inputProps={{ max: filters.endDate || new Date().toISOString().split('T')[0] }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField fullWidth label="To Date" type="date" name="endDate" size="small"
                                value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }}
                                inputProps={{ min: filters.startDate, max: new Date().toISOString().split('T')[0] }} disabled={!filters.startDate} />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button variant="contained" size="small" onClick={fetchAttendanceDetails}
                                    disabled={isLoading || !isFiltersComplete} startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                                    sx={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', textTransform: 'none', fontWeight: 700, px: 2.5 }}>
                                    Fetch
                                </Button>
                                <Button variant="outlined" size="small" color="success" onClick={exportToExcel}
                                    disabled={!isDataAvailable || isLoading} startIcon={<ExportIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                                    Export
                                </Button>
                                <Button variant="outlined" size="small" color="secondary" onClick={handlePrint}
                                    disabled={!isDataAvailable || isLoading} startIcon={<PrintIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                                    Print
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Collapse>
            </Paper>

            {attendanceDetails.length > 0 && (
                <Paper elevation={0} variant="outlined" sx={{ p: 0.9, mb: 1.2, borderRadius: 2.5 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {attendanceLegends.map(legend => (
                            <Chip key={legend.status} label={legend.label} size="small"
                                sx={{ bgcolor: legend.color, color: theme.palette.getContrastText(legend.color), fontSize: '0.7rem' }} />
                        ))}
                    </Stack>
                </Paper>
            )}

            <Paper elevation={0} variant="outlined" sx={{ flexGrow: 1, borderRadius: 1.5, overflow: 'hidden' }}>
                {isLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 1 }}>Loading...</Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ m: 2 }}>{error.message}</Alert>
                ) : showNoDataMessage ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <InfoIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                        <Typography variant="h6">No Records Found</Typography>
                        <Typography color="text.secondary">No attendance data matches your filters.</Typography>
                    </Box>
                ) : showInitialMessage ? (
                    <Box sx={{ p: 4, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>
                        <SearchIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                        <Typography variant="h6">Select Filters to View Attendance</Typography>
                        <Typography color="text.secondary">Choose Class, Section, and Date Range, then click "Fetch".</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...headerCellStyle, ...stickyColumnStyle, minWidth: 150 }}>Student</TableCell>
                                    <TableCell sx={{ ...headerCellStyle, minWidth: 50 }}>Roll</TableCell>
                                    <TableCell sx={{ ...headerCellStyle, minWidth: 50 }}>View</TableCell>
                                    {allDatesInRange.map(dateObj => (
                                        <TableCell key={getDateKey(dateObj)} sx={{ ...headerCellStyle, minWidth: 40 }}>
                                            {formatHeaderDate(dateObj)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {attendanceDetails.map((detail, index) => (
                                    <TableRow
                                        key={detail.studentId}
                                        hover
                                        sx={{
                                            bgcolor: index % 2 ? alpha(theme.palette.grey[50], 0.45) : 'inherit',
                                            '& td': { borderBottom: `1px solid ${theme.palette.divider}` },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell sx={{ ...bodyCellStyle, ...stickyColumnStyle }}>{detail.studentName?.trim()}</TableCell>
                                        <TableCell sx={bodyCellStyle}>{detail.rollNumber || '-'}</TableCell>
                                        <TableCell sx={bodyCellStyle}>
                                            <IconButton size="small" onClick={() => handleOpenModal(detail.studentName, detail.rollNumber, detail.dailyAttendance)} aria-label="view details">
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        {allDatesInRange.map(dateObj => {
                                            const dateKey = getDateKey(dateObj);
                                            const dayStatus = detail.dailyAttendance?.[dateKey] || 'A';
                                            return (
                                                <TableCell key={`${dateKey}-${detail.studentId}`} sx={{ ...bodyCellStyle, bgcolor: getAttendanceColor(dayStatus, theme) }}>
                                                    <Tooltip title={getAttendanceTooltip(dayStatus)}><span>{dayStatus}</span></Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <div style={{ display: 'none' }}>
                <AttendancePrintableTable
                    ref={printableComponentRef}
                    attendanceDetails={attendanceDetails}
                    allDatesInRange={allDatesInRange}
                    schoolName={schoolName}
                    className={classes.find(c => String(c.classId) === String(filters.class))?.className}
                    sectionName={sections.find(s => String(s.sectionId) === String(filters.section))?.sectionName}
                    fromDate={filters.startDate}
                    toDate={filters.endDate}
                />
            </div>

            <AttendanceDetailsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                entityName={`${selectedStudent.name} (Roll: ${selectedStudent.roll || '-'})`}
                dailyAttendance={selectedStudent.dailyAttendance}
                startDate={filters.startDate}
                endDate={filters.endDate}
                getAttendanceColor={status => getAttendanceColor(status, theme)}
                getAttendanceTooltip={getAttendanceTooltip}
            />

            <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification(prev => ({ ...prev, open: false }))}>
                <Alert severity={notification.severity} variant="filled">{notification.message}</Alert>
            </Snackbar>
        </Box>
    );
});

export default StudentAttendance;
